<template>
  <modal class="login-gated-resource-modal" @close="onClose">
    <h2 class="modal__title" v-html="copy.title" />
    <p class="modal__subtitle" v-html="copy.subtitle" />
    <div class="login-gated-resource-modal__main">
      <div class="login-gated-resource-modal__form">
        <login-form
          :form
          :v$
          :isValidationRun
          :isLoaderVisible
          :commonServerErrors
          submitButtonVariant="secondary-inversed-mono"
          @onValueChange="onValueChange"
          @loginRequest="submitLoginRequest"
        >
          <template #actions>
            <app-button
              type="button"
              variant="link"
              :text="$t('forgotPassword.forgotYourPassword')"
              class="login-form__forgot-password-link"
              @clicked="isForgotPasswordModalShow = true"
            />

            <transition name="modal">
              <forgot-password
                v-if="isForgotPasswordModalShow"
                :title="$t('forgotPassword.title')"
                @close="isForgotPasswordModalShow = false"
              />
            </transition>
          </template>
        </login-form>
      </div>
      <div v-if="isGuestAccessVisible" class="login-gated-resource-modal__guest-block">
        <p class="login-gated-resource-modal__text" v-html="copy.externalText" />
        <app-button
          type="button"
          size="sub-middle"
          :text="copy.externalButton"
          class="login-gated-resource-modal__button"
          @clicked="$emit('guestAccessRequested')"
        />
      </div>
    </div>
  </modal>
</template>
<script>
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength } from '@vuelidate/validators'
import { computed } from 'vue'

import { server, email } from '@/lib/utils/validationHelpers'

import { useContentfulComponent } from '@/components/common/contentful/composables/useContentfulComponent'
import Modal from '@/components/common/Modal.vue'

import ForgotPassword from '@/components/auth/ForgotPassword.vue'
import LoginForm from '@/components/auth/LoginForm.vue'

import { useFormValidation } from '@/composables/useFormValidation'
import { useSessionStore } from '@/stores/session'

export default {
  components: {
    Modal,
    LoginForm,
    ForgotPassword,
  },
  props: {
    isGuestAccessVisible: {
      type: Boolean,
      requred: true,
    },
  },
  emits: ['loggedIn', 'guestAccessRequested', 'close'],
  setup() {
    const sessionStore = useSessionStore()
    const { getSiteCopy } = useContentfulComponent()
    const v$ = useVuelidate()
    const isModelValid = computed(() => !v$.value.$invalid)

    const {
      withPresendRoutine,
      pushFormToData,
      sendForm,
      onFieldChange,
      isValidationRun,
      isLoaderVisible,
    } = useFormValidation({
      isModelValid,
    })

    return {
      sessionStore,
      getSiteCopy,
      withPresendRoutine,
      pushFormToData,
      sendForm,
      onFieldChange,
      v$,
      isValidationRun,
      isLoaderVisible,
    }
  },
  data() {
    return {
      isForgotPasswordModalShow: false,
      form: {
        email: {
          id: 'email',
          fieldLabel: 'Email Address *',
          isLabelVisible: true,
          value: '',
          title: 'username',
          type: 'email',
          maxLength: 80,
          size: 'small',
          inputStyle: 'dark-grey',
          errors: {
            required: 'Please provide an email address.',
            email: 'Please provide a valid email address.',
          },
        },
        password: {
          id: 'password',
          fieldLabel: 'Password *',
          isLabelVisible: true,
          value: '',
          title: 'password',
          type: 'password',
          maxLength: 128,
          size: 'small',
          inputStyle: 'dark-grey',
          errors: {
            required: 'Please provide a password.',
          },
        },
      },
      commonServerErrors: [],
    }
  },
  computed: {
    copy() {
      const groupKey = 'copyGroupGatedContentLoginModal'
      return {
        title:
          this.getSiteCopy(groupKey, 'copyItemGatedContentModalTitle') ||
          'Please log in to view this resource.',
        subtitle:
          this.getSiteCopy(groupKey, 'copyItemGatedContentModalSubtitle') ||
          'It looks like you’ve found exclusive content designed specifically for our registered users.',
        externalText:
          this.getSiteCopy(groupKey, 'copyItemGatedContentModalExternalText') ||
          'If you do not wish to log in, please select below:',
        externalButton:
          this.getSiteCopy(groupKey, 'copyItemGatedContentModalExternalButton') ||
          'View External Resource',
      }
    },
  },
  methods: {
    onValueChange({ field, value }) {
      this.onFieldChange(field, this.v$)
      this.form[field.id].value = value
    },
    submitLoginRequest() {
      this.withPresendRoutine(async () => {
        const loginData = {}
        this.pushFormToData(loginData, this.form)
        await this.sendForm({
          action: this.sessionStore.login,
          data: loginData,
          formData: { form: this.form, commonServerErrors: this.commonServerErrors },
        })
        this.$emit('loggedIn')
      })
    },
    onClose() {
      if (this.isForgotPasswordModalShow) {
        this.isForgotPasswordModalShow = false
      } else {
        this.$emit('close')
      }
    },
  },
  validations() {
    return {
      form: {
        email: {
          value: {
            required,
            email,
            maxLength: maxLength(80),
            server,
          },
        },
        password: {
          value: {
            required,
            maxLength: maxLength(128),
            server,
          },
        },
      },
    }
  },
}
</script>
